import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  FileText, 
  ArrowLeft, 
  Loader2, 
  RefreshCw,
  Search,
  X,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { salesOrderReportApiService, SalesOrderPerformanceDto } from '../services/salesOrderReportApi';
import { Badge } from './ui/badge';
import { getPaginationConfig } from '../config/uiConfig';
import { useBranch } from '../contexts/BranchContext';

const SalesOrderPerformanceReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [reportData, setReportData] = useState<SalesOrderPerformanceDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const paginationConfig = getPaginationConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);

  const toDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { defaultMonthStart, defaultMonthEnd } = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { defaultMonthStart: toDateInputValue(start), defaultMonthEnd: toDateInputValue(end) };
  }, []);

  const defaultMonthValue = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [selectedMonth, setSelectedMonth] = useState(defaultMonthValue);
  const [appliedDateFrom, setAppliedDateFrom] = useState(defaultMonthStart);
  const [appliedDateTo, setAppliedDateTo] = useState(defaultMonthEnd);

  const loadReport = async (from = appliedDateFrom, to = appliedDateTo) => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      setReportData([]);
      return;
    }

    try {
      setLoading(true);
      const response = await salesOrderReportApiService.getSalesOrderPerformanceReport(
        selectedBranch,
        from || undefined,
        to || undefined
      );
      
      if (response.success && response.data) {
        setReportData(response.data);
      } else {
        setReportData([]);
      }
    } catch (error) {
      // console.error('Error loading sales order performance report:', error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      void loadReport();
    }
  }, [selectedBranch, appliedDateFrom, appliedDateTo]);

  const applyMonth = async (monthValue: string) => {
    // monthValue format: YYYY-MM
    if (!monthValue || !/^\d{4}-\d{2}$/.test(monthValue)) return;
    const [yStr, mStr] = monthValue.split('-');
    const year = Number(yStr);
    const monthIndex = Number(mStr) - 1; // 0-based
    if (Number.isNaN(year) || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return;

    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0);
    const startStr = toDateInputValue(start);
    const endStr = toDateInputValue(end);

    setAppliedDateFrom(startStr);
    setAppliedDateTo(endStr);
    setCurrentPage(1);
    await loadReport(startStr, endStr);
  };

  const handleMonthChange = async (value: string) => {
    setSelectedMonth(value);
    await applyMonth(value);
  };

  const handleSetCurrentMonth = async () => {
    setSelectedMonth(defaultMonthValue);
    await applyMonth(defaultMonthValue);
  };

  const handleApplyMonth = async () => {
    if (!selectedMonth) {
      toast.error('Please select a month.');
      return;
    }
    await applyMonth(selectedMonth);
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const getStatusColor = (status: string | undefined | null): string => {
    if (!status) return 'bg-gray-100 text-gray-700';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('delivered')) {
      return 'bg-green-100 text-green-700';
    }
    if (statusLower.includes('pending') || statusLower.includes('in progress')) {
      return 'bg-yellow-100 text-yellow-700';
    }
    if (statusLower.includes('cancelled') || statusLower.includes('rejected')) {
      return 'bg-red-100 text-red-700';
    }
    return 'bg-blue-100 text-blue-700';
  };

  const getCompletionColor = (percentage: number | undefined | null): string => {
    if (percentage === null || percentage === undefined) return 'text-gray-600';
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  // Group data by sales order and calculate average completion percentage
  const aggregatedData = useMemo(() => {
    const grouped = new Map<number, SalesOrderPerformanceDto[]>();
    
    // Group by sales order ID
    reportData.forEach((item) => {
      if (item.salesOrderId) {
        if (!grouped.has(item.salesOrderId)) {
          grouped.set(item.salesOrderId, []);
        }
        grouped.get(item.salesOrderId)!.push(item);
      }
    });
    
    // Create aggregated data with average completion percentage
    const aggregated: SalesOrderPerformanceDto[] = Array.from(grouped.values()).map((items) => {
      const firstItem = items[0];
      
      // Calculate average completion percentage
      const validCompletions = items
        .map(item => item.completionPercentage)
        .filter((val): val is number => val !== null && val !== undefined && !isNaN(val));
      
      const avgCompletion = validCompletions.length > 0
        ? validCompletions.reduce((sum, val) => sum + val, 0) / validCompletions.length
        : null;
      
      // Get the latest activity date
      const activityDates = items
        .map(item => item.lastActivityDate)
        .filter((val): val is string => val !== null && val !== undefined)
        .map(date => new Date(date).getTime());
      
      const latestActivityDate = activityDates.length > 0
        ? new Date(Math.max(...activityDates)).toISOString()
        : firstItem.lastActivityDate;
      
      return {
        ...firstItem,
        completionPercentage: avgCompletion ?? undefined,
        lastActivityDate: latestActivityDate
      };
    });
    
    return aggregated;
  }, [reportData]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    let data = aggregatedData;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((item) =>
        item.salesOrderNumber?.toLowerCase().includes(searchLower) ||
        item.customerName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (sortColumn) {
      data = [...data].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortColumn) {
          case 'salesOrderNumber':
            aValue = a.salesOrderNumber || '';
            bValue = b.salesOrderNumber || '';
            break;
          case 'orderDate':
            aValue = a.orderDate ? new Date(a.orderDate).getTime() : 0;
            bValue = b.orderDate ? new Date(b.orderDate).getTime() : 0;
            break;
          case 'effectiveStartDate':
            aValue = a.effectiveStartDate ? new Date(a.effectiveStartDate).getTime() : 0;
            bValue = b.effectiveStartDate ? new Date(b.effectiveStartDate).getTime() : 0;
            break;
          case 'effectiveEndDate':
            aValue = a.effectiveEndDate ? new Date(a.effectiveEndDate).getTime() : 0;
            bValue = b.effectiveEndDate ? new Date(b.effectiveEndDate).getTime() : 0;
            break;
          case 'customerName':
            aValue = a.customerName || '';
            bValue = b.customerName || '';
            break;
          case 'completionPercentage':
            aValue = a.completionPercentage ?? 0;
            bValue = b.completionPercentage ?? 0;
            break;
          default:
            return 0;
        }
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle number comparison
        return sortDirection === 'asc' 
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      });
    }
    
    return data;
  }, [aggregatedData, searchTerm, sortColumn, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Reset to first page when search term or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        'Sales Order Number',
        'Order Date',
        'Effective Start Date',
        'Effective End Date',
        'Customer Name',
        'Completion %',
        'Last Activity Date'
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(','),
        ...filteredData.map((item) => {
          const row = [
            `"${(item.salesOrderNumber || '').replace(/"/g, '""')}"`,
            item.orderDate ? formatDate(item.orderDate) : 'N/A',
            item.effectiveStartDate ? formatDate(item.effectiveStartDate) : 'N/A',
            item.effectiveEndDate ? formatDate(item.effectiveEndDate) : 'N/A',
            `"${(item.customerName || '').replace(/"/g, '""')}"`,
            item.completionPercentage !== null && item.completionPercentage !== undefined 
              ? `${item.completionPercentage.toFixed(2)}%`
              : 'N/A',
            item.lastActivityDate ? formatDateTime(item.lastActivityDate) : 'N/A'
          ];
          return row.join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Sales_Order_Performance_Report_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${filteredData.length} records to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Sales Order Performance Report</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadReport()}
                  disabled={loading || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Content */}
        {!selectedBranch ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">Please select a branch to view the report</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : loading ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading report data...</span>
              </div>
            </CardContent>
          </Card>
        ) : reportData.length === 0 ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No data available for this report</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try refreshing or check back later
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Sales Order Performance Details
                </CardTitle>
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end w-full sm:w-auto">
                  <div className="space-y-1">
                    <Label htmlFor="soPerfMonth" className="text-xs text-gray-500">
                      Month
                    </Label>
                    <Input
                      id="soPerfMonth"
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => void handleMonthChange(e.target.value)}
                      className="w-full sm:w-48"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => void handleApplyMonth()}>
                      Apply
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => void handleSetCurrentMonth()}>
                      Current month
                    </Button>
                  </div>
                  {/* Search Input */}
                  <div className="relative max-w-md w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search by SO number or customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-10"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Table Controls */}
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {filteredData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
                  {reportData.length !== filteredData.length && (
                    <span className="text-gray-400 ml-1">
                      (filtered from {reportData.length} total)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Items per page:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paginationConfig.itemsPerPageOptions.map((option) => (
                        <SelectItem key={option} value={option.toString()}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('salesOrderNumber')}
                      >
                        <div className="flex items-center gap-2">
                          Sales Order
                          {getSortIcon('salesOrderNumber')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('orderDate')}
                      >
                        <div className="flex items-center gap-2">
                          Order Date
                          {getSortIcon('orderDate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('effectiveStartDate')}
                      >
                        <div className="flex items-center gap-2">
                          Effective Start Date
                          {getSortIcon('effectiveStartDate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('effectiveEndDate')}
                      >
                        <div className="flex items-center gap-2">
                          Effective End Date
                          {getSortIcon('effectiveEndDate')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('customerName')}
                      >
                        <div className="flex items-center gap-2">
                          Customer
                          {getSortIcon('customerName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="font-semibold text-right cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('completionPercentage')}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Completion %
                          {getSortIcon('completionPercentage')}
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold">
                        Last Activity
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No data found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((item, index) => (
                        <TableRow key={`${item.salesOrderId}-${index}`} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            {item.salesOrderId && item.salesOrderNumber ? (
                              <button
                                onClick={() => window.open(`/reports/sales-order-performance/${item.salesOrderId}`, '_blank')}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              >
                                {item.salesOrderNumber}
                              </button>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDate(item.orderDate)}
                          </TableCell>
                          <TableCell>
                            {formatDate(item.effectiveStartDate)}
                          </TableCell>
                          <TableCell>
                            {formatDate(item.effectiveEndDate)}
                          </TableCell>
                          <TableCell>
                            {item.customerName || 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={getCompletionColor(item.completionPercentage)}>
                              {item.completionPercentage !== null && item.completionPercentage !== undefined 
                                ? `${item.completionPercentage.toFixed(2)}%` 
                                : 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {formatDateTime(item.lastActivityDate)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SalesOrderPerformanceReportPage;

